import React, { useState } from 'react';
import Modal2 from 'components/commons/modal/Modal2';
import { range } from 'utils/arrays';
import Loading from 'components/commons/loading/Loading';
import { StatusProps } from 'components/features/target-achievement-support/utils/birthday';

const unregisteredValue = -1;

const UnregistedOption = () => <option value={unregisteredValue}>‐</option>;

type Props = {
  close: () => void;
  isShow: boolean;
  register: (value: Date) => Promise<{ status: StatusProps }>;
};

export const BirthDayRegisterModal = ({
  close,
  isShow,
  register,
}: Props): React.ReactElement => {
  const thisYear = new Date().getFullYear();
  const [birthday, setBirthday] = useState({
    year: thisYear - 30,
    month: unregisteredValue,
    day: unregisteredValue,
  });
  const [apiState, setAPIState] = useState<
    'not_hit' | 'hitting' | 'succeeded' | 'failed'
  >('not_hit');
  React.useEffect(() => {
    if (apiState === 'succeeded') close();
  }, [apiState, close]);

  const changeYear = (year: number): void => {
    setBirthday(prev => {
      return {
        ...prev,
        year,
      };
    });
  };

  const changeMonth = (month: number): void => {
    setBirthday(prev => {
      return {
        ...prev,
        month,
      };
    });
  };

  const changeDay = (day: number): void => {
    setBirthday(prev => {
      return {
        ...prev,
        day,
      };
    });
  };

  const isDisabledRegister =
    birthday.day === unregisteredValue || apiState === 'hitting';

  const handleRegister = async (): Promise<void> => {
    setAPIState('hitting');
    const ret = await register(
      new Date(birthday.year, birthday.month, birthday.day)
    );
    switch (ret.status) {
      case 'Success':
        setAPIState('succeeded');
        return;
      case 'Error':
        setAPIState('failed');
    }
  };

  return (
    <Modal2 isShow={isShow}>
      <div className="p-4">
        <div className={`flex flex-row justify-between`}>
          <div className="text-base font-bold">生年月日の登録</div>
        </div>
        <img
          src="/static/images/advisor/birthday-input.svg"
          alt="生年月日登録の画像"
          className="w-full mt-4"
        />
        <div className="mt-4 text-sm">
          資産目標サポートのご利用には生年月日の登録が必要です。
          <br />※ 無断で公開されることはありません。
        </div>
        <div className="mt-4 text-xs font-bold text-bluegray-450">
          あなたの生年月日
        </div>
        <div className="mt-1 flex flex-row">
          <div
            className={`w-1/3 flex flex-row items-center overflow-hidden border border-bluegray-400 rounded-lg`}>
            <select
              className={`text-sm py-2 pr-3 pl-3 font-bold w-full h-full text-bluegray-450 bg-transparent appearance-none cursor-pointer`}
              value={birthday.year}
              onChange={event => changeYear(Number(event.currentTarget.value))}>
              <UnregistedOption />
              {range(thisYear - 100, thisYear).map((x, i) => (
                <option key={i} value={x}>
                  {x}年
                </option>
              ))}
            </select>
            <div
              className={
                'text-bluegray-400 pointer-events-none icon-chevron-down p-0.5'
              }></div>
          </div>
          <div
            className={`w-1/3 ml-2 flex flex-row items-center overflow-hidden border rounded-lg ${
              birthday.year === unregisteredValue
                ? 'border-bluegray-300'
                : 'border-bluegray-400'
            }`}>
            <select
              className={`text-sm py-2 pr-5 pl-3 font-bold w-full h-full bg-transparent appearance-none ${
                birthday.year === unregisteredValue
                  ? 'text-bluegray-300'
                  : 'text-bluegray-450'
              } cursor-pointer`}
              value={birthday.month}
              onChange={event => changeMonth(Number(event.currentTarget.value))}
              disabled={birthday.year === unregisteredValue}>
              <UnregistedOption />
              {range(1, 12).map((x, i) => (
                <option key={i} value={x - 1}>
                  {x}月
                </option>
              ))}
            </select>
            <div
              className={`pointer-events-none icon-chevron-down p-0.5 ${
                birthday.year === unregisteredValue
                  ? 'text-bluegray-300'
                  : 'text-bluegray-400'
              }`}></div>
          </div>
          <div
            className={`w-1/3 ml-2 flex flex-row items-center overflow-hidden border rounded-lg ${
              birthday.month === unregisteredValue
                ? 'border-bluegray-300'
                : 'border-bluegray-400'
            }`}>
            <select
              className={`text-sm py-2 pr-5 pl-3 font-bold w-full h-ful bg-transparent appearance-none ${
                birthday.month === unregisteredValue
                  ? 'text-bluegray-300'
                  : 'text-bluegray-450'
              } cursor-pointer`}
              value={birthday.day}
              onChange={event => changeDay(Number(event.currentTarget.value))}
              disabled={birthday.month === unregisteredValue}>
              <UnregistedOption />
              {range(
                1,
                new Date(birthday.year, birthday.month + 1, 0).getDate()
              ).map((x, i) => (
                <option key={i} value={x}>
                  {x}日
                </option>
              ))}
            </select>
            <div
              className={`pointer-events-none icon-chevron-down p-0.5 ${
                birthday.month === unregisteredValue
                  ? 'text-bluegray-300'
                  : 'text-bluegray-400'
              }`}></div>
          </div>
        </div>
        <div>
          <button
            className={`mt-4 rounded-lg flex flex-row items-center justify-center p-2 w-full ${
              apiState === 'hitting' ? 'bg-gray-300' : 'bg-tradewell'
            } text-white font-bold ${isDisabledRegister ? 'opacity-20' : ''}`}
            onClick={handleRegister}
            disabled={isDisabledRegister}>
            {apiState === 'hitting' ? (
              <Loading
                loading
                style={{ width: '30px', height: '30px', color: '#444' }}
              />
            ) : (
              '登録'
            )}
          </button>
          {apiState === 'failed' && (
            <div className="text-xs text-tradewell mt-2 mx-2">
              サーバーエラーです。再度登録して下さい。
            </div>
          )}
        </div>
      </div>
    </Modal2>
  );
};
